import { Mission } from "@/types/mission/mission-type";
import { Objective } from "@/types/objective/objective-type";
import { useEffect, useRef, useState } from "react";
import { getProgressParticipantByCampaignId } from "@/services/participant/participant";
import { Participant } from "@/types/participant/participant-types";
import useFetch from "../useFetch/useFetch";
import { ALLOWED_OBJECTIVE_REQUIREMENTS } from "@/components/missions-render-v1/components/requirements-objective/utils";
import { useUserContext } from "@/contexts/user/useUserContext";

type Config = {
  filterRequirements: boolean
}

const useMission  = (
  mission: Mission, 
  participant: Participant[],
  config?: Config
) => {
  const { filterRequirements = true } = config ?? {};
  const { user, isConnect } = useUserContext();
  const haveObjectiveSurvey = mission?.objectives?.some((objective: Objective) => objective?.type?.includes('FILL_OUT_SV')) || false;
  const paramsProgress = {
    domain: import.meta.env.VITE_DOMAIN,
    mission_id: mission.id,
    user_main_key: Array.isArray(participant) && participant.length > 0 ? participant[0].user_main_key : user?.main_key,
    limit: 100,
    page: 0,
    orderBy: 'id',
    cache: !haveObjectiveSurvey
  }
  const { data: objectiveProgress, loading: loadingProgress, startFetch: getProgress } = useFetch(getProgressParticipantByCampaignId, paramsProgress);
  const [missionState, setMissionState] = useState<Mission>(mission);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    if(objectiveProgress?.length > 0){
      setMissionState((prevState)=>{
        return {
          ...prevState,
          objectives: filterRequirements ? objectiveProgress?.filter((objective: Objective) => {
            const newType = objective?.type?.split(':')?.[0];
            return !ALLOWED_OBJECTIVE_REQUIREMENTS.includes(newType);
          }) : objectiveProgress
        }
      })
    }
  }, [objectiveProgress, filterRequirements]);

  useEffect(() => {
    const validateObjectiveCompletion = (objective: Objective): boolean => {
      const { type, participant_progress, quantity } = objective;
      const { current_progress } = participant_progress ?? { current_progress: 0 };
      const isCurrentObjectiveComplete = current_progress > 0 && current_progress >= quantity;
      if (type.includes('EXCHANGE_SHARE') || type.includes('CONTEST') || type.includes('GET_SV_POINTS')) {
        return true;
      }
      if (!objective?.children || objective?.children?.length === 0) {
        return isCurrentObjectiveComplete;
      }
      const areChildrenComplete = objective?.children?.every(child => {
        return validateObjectiveCompletion(child)
      });
      return isCurrentObjectiveComplete && areChildrenComplete;
    };

    const validateIsCompletedAllObjectives = (objectives: Objective[]): boolean => {
      return objectives?.every(objective => validateObjectiveCompletion(objective));
    };

    setIsCompleted(validateIsCompletedAllObjectives(missionState?.objectives));
  }, [missionState?.objectives]);

  const firstRender = useRef<boolean>(false);
  useEffect(()=> {
    if (!loadingProgress && firstRender.current && isConnect){
      getProgress();
    }
  }, [loadingProgress, isConnect, getProgress])

	return {
    isCompleted,
    missionState, 
    getProgress,
    loadingProgress
	};
};

export { useMission } ;